import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Person } from '../../types';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import type {
  CandidateExperienceEntry,
  ExperienceEntryInput,
  ExperienceProjectInput,
} from '../../types/candidateDetails';
// @ts-ignore
import { peopleApi } from '../../services/api';

/**
 * Phase 3E-2 — full-screen editor for an external candidate's experience
 * collection. Atomic PUT /api/people/:id/experience: the entire submitted
 * array replaces the `admin_edit` shadow layer in one shot; the `cv` layer
 * is untouched and remains available via `getSourceLayers()` for comparison.
 *
 * Wireframe: docs/designs/candidate-edit-flows.svg (section B).
 *
 * MVP scope:
 *  - Entry reorder via native HTML5 drag-and-drop (matches ProjectGroup /
 *    PositionCard conventions in the rest of the app).
 *  - Nested project editing (add / remove, no reorder within an entry).
 *  - Field-level error highlighting from 400/413 responses — the backend
 *    emits a dotted `field` path like `entries[2].projects[1].tools`.
 *  - Unsaved-changes guard on close / Cancel (window.confirm).
 */

import { adminProfileApi, type ProfileMutationsApi } from '../../services/profileMutationsApi';

export interface ExperienceEditorModalProps {
  person: Person;
  apiClient?: ProfileMutationsApi;
  onClose: () => void;
  onSaved: (updated: Person) => void;
}

const EMPLOYMENT_TYPE_OPTIONS = ['fulltime', 'contract', 'freelance', 'internship', 'other'] as const;
const LOCATION_MODEL_OPTIONS = ['onsite', 'hybrid', 'remote'] as const;
const SENIORITY_OPTIONS = ['intern', 'junior', 'mid', 'senior', 'lead', 'principal'] as const;
const MANAGEMENT_OPTIONS = ['ic', 'lead', 'manager', 'director', 'vp', 'c_level'] as const;

const DRAG_TYPE = 'application/x-experience-reorder';

// ---------------------------------------------------------------------------
// Form state shapes
// ---------------------------------------------------------------------------

interface ProjectForm {
  project_name: string;
  client_or_product: string;
  start_date: string;
  end_date: string;
  summary: string;
  responsibilities: string;
  outcomes: string;
  tools: string;
}

interface EntryForm {
  /** Stable client-side id for React keys + drag identity. Unrelated to DB id. */
  localId: string;
  company_name: string;
  role_title: string;
  employment_type: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  location: string;
  location_model: string;
  summary: string;
  responsibilities: string;
  achievements: string;
  team_context: string;
  seniority_signal: string;
  management_signal: string;
  projects: ProjectForm[];
  collapsed: boolean;
}

let _localIdCounter = 0;
function nextLocalId(): string {
  _localIdCounter += 1;
  return `exp-${_localIdCounter}-${Date.now().toString(36)}`;
}

function entryFromCandidate(c: CandidateExperienceEntry): EntryForm {
  return {
    localId: nextLocalId(),
    company_name: c.companyName ?? '',
    role_title: c.roleTitle ?? '',
    employment_type: (c.employmentType as string) ?? '',
    start_date: c.startDate ?? '',
    end_date: c.endDate ?? '',
    is_current: !!c.isCurrent,
    location: c.location ?? '',
    location_model: (c.locationModel as string) ?? '',
    summary: c.summary ?? '',
    responsibilities: (c.responsibilities ?? []).join('\n'),
    achievements: (c.achievements ?? []).join('\n'),
    team_context: c.teamContext ?? '',
    seniority_signal: (c.senioritySignal as string) ?? '',
    management_signal: (c.managementSignal as string) ?? '',
    projects: [],
    collapsed: false,
  };
}

function blankEntry(): EntryForm {
  return {
    localId: nextLocalId(),
    company_name: '',
    role_title: '',
    employment_type: '',
    start_date: '',
    end_date: '',
    is_current: false,
    location: '',
    location_model: '',
    summary: '',
    responsibilities: '',
    achievements: '',
    team_context: '',
    seniority_signal: '',
    management_signal: '',
    projects: [],
    collapsed: false,
  };
}

function blankProject(): ProjectForm {
  return {
    project_name: '',
    client_or_product: '',
    start_date: '',
    end_date: '',
    summary: '',
    responsibilities: '',
    outcomes: '',
    tools: '',
  };
}

// ---------------------------------------------------------------------------
// Serialization: EntryForm -> ExperienceEntryInput for the wire.
// ---------------------------------------------------------------------------

function splitLines(raw: string): string[] {
  return raw
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

function splitCsv(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

function nullIfEmpty(s: string): string | null {
  const t = s.trim();
  return t === '' ? null : t;
}

function projectToPayload(p: ProjectForm): ExperienceProjectInput {
  return {
    project_name: nullIfEmpty(p.project_name),
    client_or_product: nullIfEmpty(p.client_or_product),
    start_date: nullIfEmpty(p.start_date),
    end_date: nullIfEmpty(p.end_date),
    summary: nullIfEmpty(p.summary),
    responsibilities: splitLines(p.responsibilities),
    outcomes: splitLines(p.outcomes),
    tools: splitCsv(p.tools),
  };
}

function entryToPayload(e: EntryForm): ExperienceEntryInput {
  return {
    company_name: nullIfEmpty(e.company_name),
    role_title: nullIfEmpty(e.role_title),
    employment_type: nullIfEmpty(e.employment_type),
    start_date: nullIfEmpty(e.start_date),
    end_date: e.is_current ? null : nullIfEmpty(e.end_date),
    is_current: e.is_current,
    location: nullIfEmpty(e.location),
    location_model: nullIfEmpty(e.location_model),
    summary: nullIfEmpty(e.summary),
    responsibilities: splitLines(e.responsibilities),
    achievements: splitLines(e.achievements),
    team_context: nullIfEmpty(e.team_context),
    seniority_signal: nullIfEmpty(e.seniority_signal),
    management_signal: nullIfEmpty(e.management_signal),
    projects: e.projects.map(projectToPayload),
  };
}

// ---------------------------------------------------------------------------
// Dotted-path helpers — backend returns fields like
// `entries[3].projects[1].tools`; this parses the indices so we can show
// an inline error on the right control.
// ---------------------------------------------------------------------------

interface ParsedErrorField {
  entryIndex: number | null;
  projectIndex: number | null;
  leaf: string;
}

function parseErrorField(field: string | undefined): ParsedErrorField | null {
  if (!field) return null;
  const m = /^entries\[(\d+)\](?:\.projects\[(\d+)\])?\.?(.*)$/.exec(field);
  if (!m) return { entryIndex: null, projectIndex: null, leaf: field };
  return {
    entryIndex: Number(m[1]),
    projectIndex: m[2] != null ? Number(m[2]) : null,
    leaf: (m[3] ?? '').replace(/^\./, ''),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ExperienceEditorModal: React.FC<ExperienceEditorModalProps> = ({
  person,
  apiClient = adminProfileApi,
  onClose,
  onSaved,
}) => {
  const initialEntries = useMemo<EntryForm[]>(() => {
    const src = person.CandidateDetails?.experiences ?? [];
    return src.map(entryFromCandidate);
  }, [person]);

  const [entries, setEntries] = useState<EntryForm[]>(initialEntries);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const draggedIndexRef = useRef<number | null>(null);
  const modalTrapRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalTrapRef, true);

  const parsedError = useMemo(() => parseErrorField(fieldError?.field), [fieldError]);

  const isDirty = useMemo(() => {
    if (entries.length !== initialEntries.length) return true;
    return JSON.stringify(entries.map(entryToPayload)) !==
      JSON.stringify(initialEntries.map(entryToPayload));
  }, [entries, initialEntries]);

  const confirmClose = useCallback(
    (nextAction: () => void) => {
      if (!isDirty) {
        nextAction();
        return;
      }
      const ok = window.confirm(
        'You have unsaved experience changes. Discard them and close the editor?'
      );
      if (ok) nextAction();
    },
    [isDirty]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') confirmClose(onClose);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmClose, onClose]);

  const patchEntry = (idx: number, mutator: (e: EntryForm) => EntryForm) => {
    setEntries((prev) => prev.map((e, i) => (i === idx ? mutator(e) : e)));
    setFieldError(null);
  };

  const addEntry = () => {
    setEntries((prev) => [...prev, blankEntry()]);
  };

  const removeEntry = (idx: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== idx));
    setFieldError(null);
  };

  const moveEntry = (from: number, to: number) => {
    if (from === to) return;
    setEntries((prev) => {
      const copy = prev.slice();
      const [item] = copy.splice(from, 1);
      copy.splice(to, 0, item);
      return copy;
    });
  };

  // ----- drag handlers -----
  const onDragStart = (idx: number) => (e: React.DragEvent<HTMLLIElement>) => {
    draggedIndexRef.current = idx;
    e.dataTransfer.setData(DRAG_TYPE, String(idx));
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (idx: number) => (e: React.DragEvent<HTMLLIElement>) => {
    if (
      !Array.from(e.dataTransfer.types).includes(DRAG_TYPE) &&
      draggedIndexRef.current == null
    )
      return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(idx);
  };
  const onDragLeave = () => setDragOverIndex(null);
  const onDrop = (idx: number) => (e: React.DragEvent<HTMLLIElement>) => {
    e.preventDefault();
    const from = draggedIndexRef.current;
    setDragOverIndex(null);
    draggedIndexRef.current = null;
    if (from == null) return;
    moveEntry(from, idx);
  };
  const onDragEnd = () => {
    draggedIndexRef.current = null;
    setDragOverIndex(null);
  };

  const handleSave = async () => {
    if (!person.id || saving) return;
    setSaving(true);
    setServerError(null);
    setFieldError(null);
    try {
      const payload = entries.map(entryToPayload);
      const updated = await apiClient.replaceExperience(person.id, payload);
      onSaved(updated);
    } catch (e: any) {
      const data = e?.response?.data ?? {};
      const message = data.error || e?.message || 'Failed to save experience';
      if (typeof data.field === 'string') {
        setFieldError({ field: data.field, message });
        // Auto-expand the offending entry and scroll it into view.
        const parsed = parseErrorField(data.field);
        if (parsed?.entryIndex != null) {
          const openIdx = parsed.entryIndex;
          setEntries((prev) =>
            prev.map((en, i) => (i === openIdx ? { ...en, collapsed: false } : en))
          );
          setTimeout(() => {
            const el = document.querySelector(
              `[data-testid="experience-editor-entry-${openIdx}"]`
            );
            if (el && typeof (el as HTMLElement).scrollIntoView === 'function') {
              (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 50);
        }
      } else {
        setServerError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={modalTrapRef}
      className="fixed inset-0 z-[70] flex flex-col bg-dark-bg"
      data-testid="experience-editor-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="experience-editor-title"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Sticky top bar */}
      <div className="flex items-center justify-between gap-4 p-4 border-b border-dark-border bg-dark-card">
        <div className="min-w-0">
          <h2 id="experience-editor-title" className="text-lg font-bold text-dark-text">
            Edit experience — {person.Name || 'Unknown'}
          </h2>
          <p className="text-xs text-dark-text-secondary mt-0.5">
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'} ·
            stored as <code className="text-amber-200">admin_edit</code> (CV re-imports
            won't overwrite).
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => confirmClose(onClose)}
            disabled={saving}
            className="px-4 py-2 rounded-md bg-dark-bg border border-dark-border text-dark-text-secondary hover:text-dark-text disabled:opacity-50"
            data-testid="experience-editor-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !isDirty}
            className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="experience-editor-save"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        {serverError && (
          <p
            className="mb-4 text-sm text-red-400"
            role="alert"
            data-testid="experience-editor-error"
          >
            {serverError}
          </p>
        )}
        {fieldError && parsedError?.entryIndex == null && (
          <p
            className="mb-4 text-sm text-red-400"
            role="alert"
            data-testid="experience-editor-field-error-global"
          >
            {fieldError.message}
          </p>
        )}

        {entries.length === 0 && (
          <div
            className="text-center py-12 text-dark-text-secondary text-sm border border-dashed border-dark-border rounded-lg"
            data-testid="experience-editor-empty"
          >
            No experience entries yet. Click "Add entry" to create one.
          </div>
        )}

        <ol className="space-y-4">
          {entries.map((entry, idx) => (
            <EntryCard
              key={entry.localId}
              index={idx}
              entry={entry}
              dragOver={dragOverIndex === idx}
              parsedError={
                parsedError?.entryIndex === idx ? parsedError : null
              }
              errorMessage={
                parsedError?.entryIndex === idx ? fieldError?.message ?? null : null
              }
              onDragStart={onDragStart(idx)}
              onDragOver={onDragOver(idx)}
              onDragLeave={onDragLeave}
              onDrop={onDrop(idx)}
              onDragEnd={onDragEnd}
              onPatch={(m) => patchEntry(idx, m)}
              onRemove={() => removeEntry(idx)}
            />
          ))}
        </ol>

        <div className="mt-4">
          <button
            type="button"
            onClick={addEntry}
            className="w-full px-4 py-3 rounded-md border-2 border-dashed border-dark-border text-dark-text-secondary hover:text-dark-text hover:border-indigo-500 text-sm font-medium"
            data-testid="experience-editor-add-entry"
          >
            + Add entry
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Entry card
// ---------------------------------------------------------------------------

interface EntryCardProps {
  index: number;
  entry: EntryForm;
  dragOver: boolean;
  parsedError: ParsedErrorField | null;
  errorMessage: string | null;
  onDragStart: (e: React.DragEvent<HTMLLIElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLLIElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent<HTMLLIElement>) => void;
  onDragEnd: () => void;
  onPatch: (mutator: (e: EntryForm) => EntryForm) => void;
  onRemove: () => void;
}

const EntryCard: React.FC<EntryCardProps> = ({
  index,
  entry,
  dragOver,
  parsedError,
  errorMessage,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onPatch,
  onRemove,
}) => {
  const leaf = parsedError && parsedError.projectIndex == null ? parsedError.leaf : '';
  const errorFor = (field: string) =>
    leaf === field && errorMessage ? errorMessage : null;

  const headline = [entry.role_title, entry.company_name].filter(Boolean).join(' · ') ||
    `New entry #${index + 1}`;

  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      data-testid={`experience-editor-entry-${index}`}
      className={`rounded-lg border bg-dark-card transition-colors ${
        dragOver ? 'border-indigo-500 bg-indigo-950/20' : 'border-dark-border'
      }`}
    >
      <div className="flex items-start gap-2 p-4">
        <span
          className="text-dark-text-secondary text-lg cursor-grab select-none mt-0.5"
          title="Drag to reorder"
          aria-label="Drag handle"
          data-testid={`experience-editor-drag-${index}`}
        >
          ⠿
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPatch((e) => ({ ...e, collapsed: !e.collapsed }))}
              className="text-sm font-bold text-dark-text truncate text-left hover:text-indigo-300"
              data-testid={`experience-editor-toggle-${index}`}
              aria-expanded={!entry.collapsed}
            >
              {entry.collapsed ? '▸' : '▾'} {headline}
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="ml-auto text-xs text-red-300 hover:text-red-200"
              data-testid={`experience-editor-remove-${index}`}
            >
              Remove
            </button>
          </div>
          {errorMessage && parsedError?.projectIndex == null && !leaf && (
            <p
              className="text-xs text-red-400 mt-1"
              role="alert"
              data-testid={`experience-editor-entry-error-${index}`}
            >
              {errorMessage}
            </p>
          )}
        </div>
      </div>

      {!entry.collapsed && (
        <div className="px-4 pb-4 space-y-4 border-t border-dark-border pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Company" error={errorFor('company_name')}>
              <input
                type="text"
                value={entry.company_name}
                onChange={(e) => onPatch((en) => ({ ...en, company_name: e.target.value }))}
                className={inputClass}
                maxLength={500}
                data-testid={`experience-editor-company-${index}`}
              />
            </Field>
            <Field label="Role title" error={errorFor('role_title')}>
              <input
                type="text"
                value={entry.role_title}
                onChange={(e) => onPatch((en) => ({ ...en, role_title: e.target.value }))}
                className={inputClass}
                maxLength={500}
                data-testid={`experience-editor-role-${index}`}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Field label="Employment type" error={errorFor('employment_type')}>
              <select
                value={entry.employment_type}
                onChange={(e) =>
                  onPatch((en) => ({ ...en, employment_type: e.target.value }))
                }
                className={inputClass}
                data-testid={`experience-editor-employment-${index}`}
              >
                <option value="">—</option>
                {EMPLOYMENT_TYPE_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Location model" error={errorFor('location_model')}>
              <select
                value={entry.location_model}
                onChange={(e) =>
                  onPatch((en) => ({ ...en, location_model: e.target.value }))
                }
                className={inputClass}
                data-testid={`experience-editor-loc-model-${index}`}
              >
                <option value="">—</option>
                {LOCATION_MODEL_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Seniority" error={errorFor('seniority_signal')}>
              <select
                value={entry.seniority_signal}
                onChange={(e) =>
                  onPatch((en) => ({ ...en, seniority_signal: e.target.value }))
                }
                className={inputClass}
                data-testid={`experience-editor-seniority-${index}`}
              >
                <option value="">—</option>
                {SENIORITY_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Management" error={errorFor('management_signal')}>
              <select
                value={entry.management_signal}
                onChange={(e) =>
                  onPatch((en) => ({ ...en, management_signal: e.target.value }))
                }
                className={inputClass}
                data-testid={`experience-editor-mgmt-${index}`}
              >
                <option value="">—</option>
                {MANAGEMENT_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Start date (YYYY-MM-DD)" error={errorFor('start_date')}>
              <input
                type="text"
                value={entry.start_date}
                onChange={(e) => onPatch((en) => ({ ...en, start_date: e.target.value }))}
                className={inputClass}
                placeholder="2021-06 or 2021-06-01"
                data-testid={`experience-editor-start-${index}`}
              />
            </Field>
            <Field label="End date" error={errorFor('end_date')}>
              <input
                type="text"
                value={entry.end_date}
                onChange={(e) => onPatch((en) => ({ ...en, end_date: e.target.value }))}
                disabled={entry.is_current}
                className={`${inputClass} ${entry.is_current ? 'opacity-50' : ''}`}
                placeholder="2023-12"
                data-testid={`experience-editor-end-${index}`}
              />
            </Field>
            <Field label="Currently here" error={null}>
              <label className="inline-flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  checked={entry.is_current}
                  onChange={(e) =>
                    onPatch((en) => ({
                      ...en,
                      is_current: e.target.checked,
                      end_date: e.target.checked ? '' : en.end_date,
                    }))
                  }
                  data-testid={`experience-editor-current-${index}`}
                />
                <span className="text-sm text-dark-text">{entry.is_current ? 'Yes' : 'No'}</span>
              </label>
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Location" error={errorFor('location')}>
              <input
                type="text"
                value={entry.location}
                onChange={(e) => onPatch((en) => ({ ...en, location: e.target.value }))}
                className={inputClass}
                maxLength={500}
                data-testid={`experience-editor-location-${index}`}
              />
            </Field>
            <Field label="Team context" error={errorFor('team_context')}>
              <input
                type="text"
                value={entry.team_context}
                onChange={(e) => onPatch((en) => ({ ...en, team_context: e.target.value }))}
                className={inputClass}
                maxLength={500}
                data-testid={`experience-editor-team-${index}`}
              />
            </Field>
          </div>

          <Field label="Summary" error={errorFor('summary')}>
            <textarea
              value={entry.summary}
              onChange={(e) => onPatch((en) => ({ ...en, summary: e.target.value }))}
              rows={2}
              className={`${inputClass} resize-y`}
              maxLength={4000}
              data-testid={`experience-editor-summary-${index}`}
            />
          </Field>

          <Field
            label="Responsibilities (one per line, max 20)"
            error={errorFor('responsibilities') || leafMatchesArray('responsibilities', leaf, errorMessage)}
          >
            <textarea
              value={entry.responsibilities}
              onChange={(e) =>
                onPatch((en) => ({ ...en, responsibilities: e.target.value }))
              }
              rows={3}
              className={`${inputClass} resize-y`}
              data-testid={`experience-editor-resp-${index}`}
            />
          </Field>

          <Field
            label="Achievements (one per line, max 20)"
            error={errorFor('achievements') || leafMatchesArray('achievements', leaf, errorMessage)}
          >
            <textarea
              value={entry.achievements}
              onChange={(e) =>
                onPatch((en) => ({ ...en, achievements: e.target.value }))
              }
              rows={3}
              className={`${inputClass} resize-y`}
              data-testid={`experience-editor-ach-${index}`}
            />
          </Field>

          <ProjectsEditor
            entryIndex={index}
            projects={entry.projects}
            parsedError={parsedError}
            errorMessage={errorMessage}
            onPatch={(mut) =>
              onPatch((en) => ({ ...en, projects: mut(en.projects) }))
            }
          />
        </div>
      )}
    </li>
  );
};

function leafMatchesArray(arr: string, leaf: string, message: string | null): string | null {
  if (!message) return null;
  const re = new RegExp(`^${arr}\\[\\d+\\]$`);
  return re.test(leaf) ? message : null;
}

// ---------------------------------------------------------------------------
// Projects editor (nested under an entry)
// ---------------------------------------------------------------------------

interface ProjectsEditorProps {
  entryIndex: number;
  projects: ProjectForm[];
  parsedError: ParsedErrorField | null;
  errorMessage: string | null;
  onPatch: (mutator: (projects: ProjectForm[]) => ProjectForm[]) => void;
}

const ProjectsEditor: React.FC<ProjectsEditorProps> = ({
  entryIndex,
  projects,
  parsedError,
  errorMessage,
  onPatch,
}) => {
  const updateProject = (pIdx: number, m: (p: ProjectForm) => ProjectForm) => {
    onPatch((prev) => prev.map((p, i) => (i === pIdx ? m(p) : p)));
  };
  const removeProject = (pIdx: number) => {
    onPatch((prev) => prev.filter((_, i) => i !== pIdx));
  };
  const addProject = () => {
    onPatch((prev) => [...prev, blankProject()]);
  };

  return (
    <div className="pt-2 border-t border-dark-border/50">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-dark-text-secondary">
          Projects ({projects.length})
        </h4>
        <button
          type="button"
          onClick={addProject}
          className="text-xs text-indigo-300 hover:text-indigo-200"
          data-testid={`experience-editor-add-project-${entryIndex}`}
        >
          + Add project
        </button>
      </div>

      {projects.length === 0 && (
        <p className="text-xs text-dark-text-secondary italic">No projects.</p>
      )}

      <ol className="space-y-3">
        {projects.map((proj, pIdx) => {
          const isErrorHere =
            parsedError?.entryIndex === entryIndex && parsedError.projectIndex === pIdx;
          const leaf = isErrorHere ? parsedError.leaf : '';
          const errorFor = (field: string) =>
            leaf === field && errorMessage ? errorMessage : null;
          return (
            <li
              key={pIdx}
              className={`rounded-md border p-3 bg-dark-bg/40 ${
                isErrorHere ? 'border-red-500' : 'border-dark-border'
              }`}
              data-testid={`experience-editor-project-${entryIndex}-${pIdx}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold text-dark-text">Project #{pIdx + 1}</span>
                <button
                  type="button"
                  onClick={() => removeProject(pIdx)}
                  className="ml-auto text-xs text-red-300 hover:text-red-200"
                  data-testid={`experience-editor-remove-project-${entryIndex}-${pIdx}`}
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Project name" error={errorFor('project_name')}>
                  <input
                    type="text"
                    value={proj.project_name}
                    onChange={(e) =>
                      updateProject(pIdx, (p) => ({ ...p, project_name: e.target.value }))
                    }
                    className={inputClass}
                    maxLength={500}
                    data-testid={`experience-editor-project-name-${entryIndex}-${pIdx}`}
                  />
                </Field>
                <Field label="Client / product" error={errorFor('client_or_product')}>
                  <input
                    type="text"
                    value={proj.client_or_product}
                    onChange={(e) =>
                      updateProject(pIdx, (p) => ({ ...p, client_or_product: e.target.value }))
                    }
                    className={inputClass}
                    maxLength={500}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                <Field label="Start date" error={errorFor('start_date')}>
                  <input
                    type="text"
                    value={proj.start_date}
                    onChange={(e) =>
                      updateProject(pIdx, (p) => ({ ...p, start_date: e.target.value }))
                    }
                    className={inputClass}
                    placeholder="YYYY-MM"
                  />
                </Field>
                <Field label="End date" error={errorFor('end_date')}>
                  <input
                    type="text"
                    value={proj.end_date}
                    onChange={(e) =>
                      updateProject(pIdx, (p) => ({ ...p, end_date: e.target.value }))
                    }
                    className={inputClass}
                    placeholder="YYYY-MM"
                  />
                </Field>
              </div>
              <div className="mt-3">
                <Field label="Summary" error={errorFor('summary')}>
                  <textarea
                    value={proj.summary}
                    onChange={(e) =>
                      updateProject(pIdx, (p) => ({ ...p, summary: e.target.value }))
                    }
                    rows={2}
                    className={`${inputClass} resize-y`}
                    maxLength={4000}
                  />
                </Field>
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label="Responsibilities (one per line)"
                  error={errorFor('responsibilities') || leafMatchesArray('responsibilities', leaf, errorMessage)}
                >
                  <textarea
                    value={proj.responsibilities}
                    onChange={(e) =>
                      updateProject(pIdx, (p) => ({ ...p, responsibilities: e.target.value }))
                    }
                    rows={3}
                    className={`${inputClass} resize-y`}
                  />
                </Field>
                <Field
                  label="Outcomes (one per line)"
                  error={errorFor('outcomes') || leafMatchesArray('outcomes', leaf, errorMessage)}
                >
                  <textarea
                    value={proj.outcomes}
                    onChange={(e) =>
                      updateProject(pIdx, (p) => ({ ...p, outcomes: e.target.value }))
                    }
                    rows={3}
                    className={`${inputClass} resize-y`}
                  />
                </Field>
              </div>
              <div className="mt-3">
                <Field
                  label="Tools (comma or line-separated, max 30)"
                  error={errorFor('tools') || leafMatchesArray('tools', leaf, errorMessage)}
                >
                  <input
                    type="text"
                    value={proj.tools}
                    onChange={(e) =>
                      updateProject(pIdx, (p) => ({ ...p, tools: e.target.value }))
                    }
                    className={inputClass}
                    placeholder="React, TypeScript, Postgres"
                    data-testid={`experience-editor-project-tools-${entryIndex}-${pIdx}`}
                  />
                </Field>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

const inputClass =
  'w-full rounded-md bg-dark-bg border border-dark-border px-3 py-2 text-sm text-dark-text placeholder-dark-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-indigo-500';

const Field: React.FC<{
  label: string;
  error: string | null;
  children: React.ReactNode;
}> = ({ label, error, children }) => (
  <div>
    <label className="block text-xs font-medium text-dark-text-secondary mb-1.5">
      {label}
    </label>
    {children}
    {error && (
      <p className="text-xs text-red-400 mt-1" role="alert">
        {error}
      </p>
    )}
  </div>
);

/** Internal helpers exported for unit tests only. */
export const __test__ = {
  entryFromCandidate,
  entryToPayload,
  projectToPayload,
  parseErrorField,
  splitLines,
  splitCsv,
};
